import { autoinject, bindable, bindingMode, containerless, observable } from "aurelia-framework";
import moment from "moment";

import nameof from "../../../common/nameof";
import { IEnumResponse } from "../../../interfaces/i-enum";
import { IAgencyTaskLeanResult } from "../../../interfaces/i-get-agency-task";
import { IGetTaskFrequenciesResult } from "../../../interfaces/patients/i-get-task-frequencies";
import { EnumsService } from "../../../services/enums-service";
import { FrequencyService } from "../../../services/frequency-service";

@containerless
@autoinject
export class FrequencyCheck {
    @bindable({ defaultBindingMode: bindingMode.toView })
    public patientId: string;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public isOutsideFrequency: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    @observable({ changeHandler: nameof<FrequencyCheck>("selectedTaskChanged") })
    public selectedTask: IAgencyTaskLeanResult;
    @bindable({ defaultBindingMode: bindingMode.toView })
    @observable({ changeHandler: nameof<FrequencyCheck>("visitDatesChanged") })
    public visitDates: string[] = [];
    private _frequencyService: FrequencyService;
    private _enumsService: EnumsService;
    private _disciplines: IEnumResponse[] = [];
    public availableFrequencies: IGetTaskFrequenciesResult[] = [];
    public isError: boolean = false;
    public isLoading: boolean = false;
    public disciplineName: string;

    public constructor(frequencyService: FrequencyService, enumsService: EnumsService) {
        this._frequencyService = frequencyService;
        this._enumsService = enumsService;
    }

    public async attached() {
        this._disciplines = await this._enumsService.getDisciplines();
        await this.checkFrequencyCompliance();
    }

    public async selectedTaskChanged() {
        if (!this.selectedTask) {
            return;
        }
        if (this._disciplines.length > 0) {
            let selectedDiscipline = this._disciplines.find(
                (discipline) => discipline.value === this.selectedTask.discipline
            );
            this.disciplineName = selectedDiscipline ? selectedDiscipline.name : "";
        }
        await this.checkFrequencyCompliance();
    }

    public async visitDatesChanged() {
        await this.checkFrequencyCompliance();
    }

    private async checkFrequencyCompliance() {
        try {
            this.isError = false;
            this.isLoading = true;
            let hasStartDates = this.visitDates && this.visitDates.length > 0;
            let isTaskIncludedInFreq = this.selectedTask && this.selectedTask.isIncludedInFrequency;
            if (isTaskIncludedInFreq && hasStartDates) {
                let response = await this._frequencyService.getTaskFrequency({
                    patientId: this.patientId,
                    isPrn: this.selectedTask.isPrn,
                    discipline: this.selectedTask.discipline,
                    visitDates: this.visitDates
                });
                let withInFreqByDate = new Map<string, IGetTaskFrequenciesResult>();
                let withinFreqDates: string[] = [];
                response.forEach((item) => {
                    let mVisitDate = moment(item.visitDate).format("MM/DD/YYYY");
                    withInFreqByDate.set(mVisitDate, item);
                    withinFreqDates.push(mVisitDate);
                });
                this.availableFrequencies = this.visitDates.map((visitDate) => {
                    let visitOutsideFreq: IGetTaskFrequenciesResult = {
                        visitDate: null,
                        frequency: null,
                        frequencyId: null,
                        startDate: null,
                        endDate: null
                    };
                    if (withinFreqDates.includes(visitDate)) {
                        return withInFreqByDate.get(visitDate);
                    } else {
                        return Object.assign(visitOutsideFreq, { visitDate });
                    }
                });
                this.isOutsideFrequency = this.availableFrequencies.some(
                    (availableFrequency) => !availableFrequency.frequency
                );
            }
        } catch (e) {
            console.error(e);
            this.isError = true;
        } finally {
            this.isLoading = false;
        }
    }

    public deleteVisitDate(visitDate: string) {
        this.visitDates = this.visitDates.filter((item) => item !== visitDate);
    }
}
